import { useSnackbar } from 'notistack'
import { useCallback, useMemo } from 'react'
import { IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import useGetErrorMessage from '../error-message/error-message'
import { useTranslation } from 'react-i18next'

export default function useErrorSnackBar(prefix?: string, suffix?: string) {
  const { defaultPrefix, defaultSuffix } = useMemo(() => ({ defaultPrefix: prefix, defaultSuffix: suffix }), [prefix, suffix])

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const getErrorMessages = useGetErrorMessage()
  const showError = useCallback(
    (e: unknown, prefix?: string, suffix?: string, isWarning?: boolean) => {
      const messages = getErrorMessages(e)
      const header = prefix || defaultPrefix
      const footer = suffix || defaultSuffix
      enqueueSnackbar(<ErrorHandlerMessages {...{ messages, header, footer }} />, {
        autoHideDuration: 2000,
        variant: isWarning ? 'warning' : 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center',
        },
        action: (key) => {
          return (
            <IconButton
              aria-label="close"
              color="inherit"
              sx={{ p: 0.5 }}
              onClick={() => {
                closeSnackbar(key)
              }}
            >
              <CloseIcon />
            </IconButton>
          )
        },
      })
    },
    [enqueueSnackbar, defaultPrefix, defaultSuffix],
  )

  return showError
}

function ErrorHandlerMessages({ messages, header, footer }: { messages: string[]; header?: string; footer?: string }) {
  const { t } = useTranslation()
  return (
    <div>
      {(header ? true : false) && (
        <Typography component="div" fontSize="14px">
          {header}
        </Typography>
      )}

      {messages.length === 0 && (
        <Typography component="div" fontSize="14px">
          {t('pms.core_error.unexpected_error')}
        </Typography>
      )}

      {messages.length >= 1 &&
        messages.map((m, i) => (
          <Typography key={i} component="div" fontSize="14px">
            {m}
          </Typography>
        ))}

      {(footer ? true : false) && (
        <Typography component="div" fontSize="14px">
          {footer}
        </Typography>
      )}
    </div>
  )
}
